import { useState, type CSSProperties } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import Modal from '../../ui/Modal'
import { usersAPI } from '../../../api/users'
import { useAuthStore } from '../../../store/authStore'
import type { Livreur } from '../../../types'

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px',
  borderRadius: 10, border: '1.5px solid #E5E7EB',
  fontSize: 14, fontFamily: "'DM Sans', sans-serif",
  outline: 'none', background: '#FAFAFA', color: '#0D1117',
  boxSizing: 'border-box',
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 13,
  fontWeight: 600, color: '#374151', marginBottom: 6,
}

const Field = ({ label, value, onChange, type = 'text', placeholder }: {
  label: string; value: string; onChange: (v: string) => void
  type?: string; placeholder?: string
}) => (
  <div style={{ marginBottom: 14 }}>
    <label style={labelStyle}>{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={inputStyle}
    />
  </div>
)

interface Props {
  profile: Livreur | null
  onClose: () => void
}

export const EditProfilModal = ({ profile, onClose }: Props) => {
  const queryClient = useQueryClient()
  const { user, setUser } = useAuthStore()

  const [name,    setName]    = useState(profile?.name   ?? user?.name   ?? '')
  const [phone,   setPhone]   = useState(profile?.phone  ?? user?.phone  ?? '')
  const [email,   setEmail]   = useState(profile?.email  ?? '')
  const [vType,   setVType]   = useState(profile?.vehicle?.type  ?? '')
  const [vBrand,  setVBrand]  = useState(profile?.vehicle?.brand ?? '')
  const [vModel,  setVModel]  = useState(profile?.vehicle?.model ?? '')
  const [vPlate,  setVPlate]  = useState(profile?.vehicle?.plate ?? '')
  const [loading, setLoading] = useState(false)

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Le nom est requis'); return }
    setLoading(true)
    try {
      await usersAPI.updateMe({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        vehicle: {
          type:  vType  || undefined,
          brand: vBrand || undefined,
          model: vModel || undefined,
          plate: vPlate || undefined,
        },
      })
      queryClient.invalidateQueries({ queryKey: ['profile-me'] })
      if (user) setUser({ ...user, name: name.trim(), phone: phone.trim() }, useAuthStore.getState().accessToken ?? '')
      toast.success('Profil mis à jour')
      onClose()
    } catch {
      toast.error('Erreur lors de la mise à jour du profil')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open title="Modifier mon profil" onClose={onClose} size="md">
      <div className="detail-grid" style={{ '--detail-cols': '1fr 1fr', gap: '0 16px' } as CSSProperties}>
        <Field label="Nom complet *" value={name} onChange={setName} placeholder="Prénom Nom" />
        <Field label="Téléphone *" value={phone} onChange={setPhone} type="tel" placeholder="+216 XX XXX XXX" />
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Email" value={email} onChange={setEmail} type="email" placeholder="email@exemple.com" />
        </div>
      </div>

      <div style={{ marginTop: 4, marginBottom: 14, borderTop: '1px solid #F3F4F6', paddingTop: 16 }}>
        <div style={{ ...labelStyle, marginBottom: 12, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: '#9CA3AF' }}>
          Véhicule
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Type</label>
          <select
            value={vType}
            onChange={e => setVType(e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            <option value="">— Sélectionner —</option>
            <option value="moto">Moto</option>
            <option value="voiture">Voiture</option>
            <option value="camionnette">Camionnette</option>
            <option value="camion">Camion</option>
          </select>
        </div>
        <div className="detail-grid" style={{ '--detail-cols': '1fr 1fr', gap: '0 16px' } as CSSProperties}>
          <Field label="Marque" value={vBrand} onChange={setVBrand} placeholder="Honda, Toyota…" />
          <Field label="Modèle" value={vModel} onChange={setVModel} placeholder="CB 125, Yaris…" />
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Plaque d'immatriculation" value={vPlate} onChange={setVPlate} placeholder="215 TUN 4582" />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={onClose}
          style={{
            flex: 1, padding: '12px 20px', borderRadius: 12,
            border: 'none', background: '#F3F4F6', color: '#374151',
            fontSize: 14, fontWeight: 600, cursor: 'pointer',
            fontFamily: "'DM Sans', sans-serif",
          }}
        >Annuler</button>
        <button
          onClick={handleSave}
          disabled={loading}
          style={{
            flex: 1, padding: '12px 20px', borderRadius: 12,
            border: 'none', background: '#1D9E75', color: '#fff',
            fontSize: 14, fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            fontFamily: "'DM Sans', sans-serif",
          }}
        >{loading ? 'Enregistrement…' : 'Enregistrer'}</button>
      </div>
    </Modal>
  )
}
